import { HelpCircle } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Tabs, TabsList, TabsTrigger } from '@/components/tabs/tabs'

import { useDialog } from '@/lib/hooks/use-dialog'
import { cn } from '@/lib/utils'

import { SEARCH_FILTER_OPTION_ITEMS, SELECT_FILTER_OPTION_VALUE_MAP, type SelectFilterOptionValue } from '../constants'
import { type SelectFilterProps } from '../select-filter'
import { ClearButton } from './clear-button'
import { OPTION_ITEM_HEIGHT } from './constants'
import { FilterInput } from './filter-input'
import { FilterItems } from './filter-items'
import { SearchOptionsGuideDialog } from './search-options-guide-dialog'

const OPTION_ITEM_CONTENT_PADDING = 4

export interface FilterContentProps {
  disabled: boolean
  items: SelectFilterProps['items']
  hideCount?: boolean
  values: SelectFilterProps['values']
  showsFilterOption?: boolean
  filterOption?: SelectFilterProps['filterOption']
  onChangeFilter: (options: string[]) => void
  onChangeSelectOption: (option: SelectFilterOptionValue) => void
}

export const FilterContent = ({
  disabled,
  items,
  hideCount,
  values,
  showsFilterOption = false,
  filterOption = SELECT_FILTER_OPTION_VALUE_MAP.or,
  onChangeFilter,
  onChangeSelectOption,
}: FilterContentProps) => {
  const [input, setInput] = useState('')

  const itemsFiltered = useMemo(() => {
    return items.filter((x) => x.filterName.toLowerCase().includes(input.toLowerCase()))
  }, [items, input])

  const {
    isDialogOpen: isOpenSearchOptionsGuideDialog,
    openDialog: openSearchOptionsGuideDialog,
    closeDialog: closeSearchOptionsGuideDialog,
  } = useDialog()

  return (
    <div>
      <FilterInput disabled={items.length <= 5} value={input} onChange={setInput} />

      {showsFilterOption && (
        <div className={cn('flex items-center justify-between', 'px-4 py-1', 'shadow-sm')}>
          <Tabs value={filterOption} onValueChange={(value) => onChangeSelectOption(value as SelectFilterOptionValue)}>
            <TabsList>
              {SEARCH_FILTER_OPTION_ITEMS.map(({ label, value }) => (
                <TabsTrigger key={value} value={value}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <button type="button" onClick={openSearchOptionsGuideDialog}>
            <HelpCircle size={16} className={cn('text-slate-400')} />
          </button>

          <SearchOptionsGuideDialog isOpen={isOpenSearchOptionsGuideDialog} onClose={closeSearchOptionsGuideDialog} />
        </div>
      )}

      <div
        className={cn('overflow-y-auto px-1')}
        style={{
          paddingTop: OPTION_ITEM_CONTENT_PADDING,
          paddingBottom: OPTION_ITEM_CONTENT_PADDING,
          height: 5 * OPTION_ITEM_HEIGHT + 2 * OPTION_ITEM_CONTENT_PADDING + (5 - 1),
        }}
      >
        <FilterItems
          disabled={disabled}
          hideCount={hideCount}
          items={itemsFiltered}
          values={values}
          onChange={onChangeFilter}
        />
      </div>

      {values && values.length > 0 && (
        <ClearButton
          disabled={disabled}
          onClick={() => {
            onChangeFilter([])
          }}
        />
      )}
    </div>
  )
}
